import { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import CryptoJS from "crypto-js";

import { fetchStudentFolderContents } from "../../utils/studentFolderContents";
import { DarkModeContext } from "../../Context/DarkModeContext";
import { GeoContext } from "../../Context/GeoContext.js";
import CourseCard from "../../Components/CourseCard";
import CoursePurchaseModal from "../../Components/CoursePurchaseModal";

export default function SubfoldersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const folder = state?.folder;

  const { folderPath } = useParams();
  const folderId = folder?.id || parseInt(folderPath?.split("/").pop());
  const folderName = folder?.name;

  const { isDarkMode } = useContext(DarkModeContext);
  const { store } = useContext(GeoContext);

  const storeId = store?.id;
  const currency = store?.currency;

  const userId = localStorage.getItem("userId");

  const parentFolder = location.state?.folder || null;
  const folderForNavigation = parentFolder || { id: folderId, name: "Folder" };

  const [subfolders, setSubfolders] = useState({ folders: [], courses: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isConfirmPurchaseModalOpen, setConfirmPurchaseModalOpen] =
    useState(false);

  // Decrypt utility
  const decryptData = (encryptedData) => {
    const bytes = CryptoJS.AES.decrypt(
      encryptedData,
      process.env.REACT_APP_SECRET_KEY,
    );
    return bytes.toString(CryptoJS.enc.Utf8);
  };

  const loadFolderData = useCallback(
    async (signal) => {
      if (!storeId) return;

      setLoading(true);
      setSubfolders({ folders: [], courses: [] });
      setTotalPages(1);

      try {
        const folderRes = await fetchStudentFolderContents(
          decryptData(userId || ""),
          folderId,
          {
            parentFolderId: folderId,
            page,
            pageSize,
            storeId,
            search,
          },
          signal,
        );
        if (signal.aborted) return;

        const folderContents = folderRes.data.response || [];
        const folders = folderContents.filter((i) => i.type === "Folder");
        const courses = folderContents.filter((i) => i.type === "Course");

        setSubfolders({ folders, courses });
        setTotalPages(Math.ceil(folderRes.data.totalCount / pageSize));
      } catch (err) {
        if (!signal.aborted && err.name !== "CanceledError") {
          toast.error(
            err.response ? "Failed to load folder contents." : err.message,
          );
        }
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [folderId, storeId, page, pageSize, userId, search],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadFolderData(controller.signal);
    return () => controller.abort();
  }, [loadFolderData]);

  const handleFolderClick = (subfolder) => {
    const subfolderSlug = subfolder.name
      ?.toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\p{L}\p{N}-]+/gu, "");

    const currentPath = location.pathname
      .replace("/student-folders/", "")
      .replace("/subfolders", "");

    const newPath = currentPath
      ? `${currentPath}/${subfolderSlug}`
      : subfolderSlug;

    navigate(`/student-folders/${newPath}`, {
      state: { folder: subfolder },
    });
  };

  const handleCourseClick = (course) => {
    if (!course.purchased) return;

    const courseName = encodeURIComponent(
      course.name.replace(/\s+/g, "_").replace(/\//g, "-").toLowerCase(),
    );

    navigate(`/student-folders/${folderName}/courses/${courseName}`, {
      state: { courseId: course.id, course, folder: folderForNavigation },
    });
  };

  const handleOpenPurchaseModal = (course) => {
    setSelectedCourse(course);
    setConfirmPurchaseModalOpen(true);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const goToNextPage = () => navigate(+1);
  const goToPreviousPage = () => navigate(-1);

  const formattedPath =
    location.pathname
      .split("/")
      .filter(Boolean)
      .map((segment) =>
        decodeURIComponent(segment)
          .replace(/[_-]+/g, " ")
          .replace(/\b[a-z]/g, (char) => char.toUpperCase())
          .trim(),
      )
      .join(" > ") + " > ";

  return (
    <div
      className={`flex flex-col min-h-screen ${
        isDarkMode ? "bg-[#181818] text-white" : "bg-customBlue text-black"
      }`}
    >
      <main className="min-w-0 flex-1 px-4 py-8 sm:px-8">
        <div
          className={`relative animate-fadeIn px-5 pb-8 pt-10 sm:px-8 rounded-lg shadow-lg transition-all duration-300 ${
            isDarkMode ? "bg-[#1e1e1e]" : "bg-white"
          }`}
        >
          {/* PATH & CONTROLS */}
          <div className="flex w-full flex-col gap-4 mb-4 text-gray-700 text-sm capitalize xl:flex-row xl:items-center xl:justify-between">
            <div className="flex min-w-0 flex-wrap gap-1 items-center">
              <button
                onClick={goToPreviousPage}
                className="w-[24px] h-[20px] flex justify-center items-center bg-gray-300 rounded-[6px] disabled:opacity-50"
              >
                <IoIosArrowBack />
              </button>
              <button
                onClick={goToNextPage}
                className="w-[24px] h-[20px] flex justify-center items-center bg-gray-300 rounded-[6px] disabled:opacity-50"
              >
                <IoIosArrowForward />
              </button>
              <div
                className={`${
                  isDarkMode ? "text-white" : "text-black"
                } min-w-0 break-words text-sm capitalize px-2`}
              >
                {formattedPath}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 py-2 lg:flex-row lg:gap-0 items-center lg:justify-end justify-center">
            <input
              type="text"
              placeholder="Search folders or courses..."
              aria-label="Search folders or courses"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${
                isDarkMode
                  ? "bg-[#292929] border border-gray-500 text-white"
                  : "bg-gray-100 border border-gray-300 text-black"
              } px-4 py-2 rounded-lg w-full min-w-0 max-w-[680px]`}
            />
          </div>

          <div
            className={`relative rounded-lg ${
              isDarkMode ? "bg-[#1e1e1e] text-white" : "bg-white text-white"
            } h-[clamp(320px,65dvh,578px)] ${loading ? "overflow-y-hidden" : "overflow-y-auto"} custom-scrollbar`}
          >
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <ClipLoader color="#2264E5" size={50} />
              </div>
            ) : subfolders.folders.length === 0 &&
              subfolders.courses.length === 0 ? (
              <p className="text-center text-gray-500 mt-12">
                No content found.
              </p>
            ) : (
              <div className="space-y-4 animate-zoomIn">
                {/* Subfolders */}
                {subfolders.folders.length > 0 && (
                  <>
                    <h3
                      className={`${
                        isDarkMode ? "text-white" : "text-black"
                      } text-xl font-semibold mb-4`}
                    >
                      📁 Subfolders
                    </h3>
                    <div className="mx-auto grid w-full max-w-[252px] grid-cols-1 items-start gap-6 p-4 md:max-w-[740px] md:grid-cols-3 lg:max-w-[1528px] lg:grid-cols-5">
                      {subfolders.folders.map((sub) => (
                        <div
                          key={sub.id}
                          onClick={() => handleFolderClick(sub)}
                          className={`cursor-pointer rounded-2xl p-6 shadow-md transform transition duration-300 hover:scale-105 ${
                            isDarkMode
                              ? "bg-gradient-to-br from-gray-800 to-gray-700 hover:shadow-gray-600/40"
                              : "bg-gradient-to-br from-blue-400 to-blue-600 hover:shadow-blue-400/40"
                          }`}
                        >
                          <div className="text-5xl mb-4">📁</div>
                          <h3 className="text-lg font-semibold truncate mb-2">
                            {sub.name}
                          </h3>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Courses */}
                {subfolders.courses.length > 0 && (
                  <>
                    <h3
                      className={`flex items-center gap-2 ${
                        isDarkMode ? "text-white" : "text-black"
                      } text-xl font-semibold mb-4`}
                    >
                      📘 Courses
                    </h3>
                    <div className="mx-auto grid w-full max-w-[246px] grid-cols-1 items-start gap-6 p-2 md:max-w-[754px] md:grid-cols-3 lg:max-w-[1512px] lg:grid-cols-5">
                      {subfolders.courses.map((course) => (
                        <CourseCard
                          key={course.id}
                          course={course}
                          currency={currency}
                          isDarkMode={isDarkMode}
                          onOpen={() => handleCourseClick(course)}
                          onPurchase={() => handleOpenPurchaseModal(course)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Pagination & PageSize */}
          <div className="flex flex-wrap gap-3 items-center justify-end py-4 text-black">
            <label
              htmlFor="pageSize"
              className={`${
                isDarkMode ? "text-white" : "text-gray-700"
              } mr-2 text-sm`}
            >
              per page:
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={handlePageSizeChange}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            >
              {[10, 30, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap justify-center gap-1 pt-4">
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index + 1}
                onClick={() => handlePageChange(index + 1)}
                className={`px-4 py-2 ${
                  page === index + 1
                    ? isDarkMode
                      ? "bg-customRed text-white"
                      : "bg-customBlue text-white"
                    : isDarkMode
                      ? "bg-gray-300 text-black"
                      : "bg-gray-300"
                } rounded-[5px] mx-1 my-1`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </main>
      {isConfirmPurchaseModalOpen && selectedCourse && (
        <CoursePurchaseModal
          course={selectedCourse}
          currency={currency}
          isDarkMode={isDarkMode}
          onClose={() => {
            setConfirmPurchaseModalOpen(false);
            setSelectedCourse(null);
          }}
          onPurchased={(course) => {
            setSubfolders((items) => ({
              ...items,
              courses: items.courses.map((item) =>
                item.id === course.id ? course : item,
              ),
            }));
          }}
          onStart={handleCourseClick}
        />
      )}
    </div>
  );
}
